"use strict";

import Vue from "vue";
import axios from "axios";

let echoInstance = null;
let pusherInitialized = false;
let axiosInterceptorId = null;

const initializePusher = () => {
  if (pusherInitialized) return;

  const Pusher = require("pusher-js");
  window.Pusher = Pusher;
  pusherInitialized = true;
};

// Remove the X-Socket-Id interceptor that Laravel Echo adds to axios
const removeEchoAxiosInterceptor = () => {
  if (axiosInterceptorId !== null) {
    window.axios?.interceptors?.request?.eject(axiosInterceptorId);
    axiosInterceptorId = null;
  }

  // Also check if there's an interceptor adding X-Socket-Id and remove it
  if (window.axios?.interceptors?.request?.handlers) {
    window.axios.interceptors.request.handlers.forEach((handler, index) => {
      if (handler && handler.fulfilled) {
        const fnStr = handler.fulfilled.toString();
        if (fnStr.includes('X-Socket-Id') || fnStr.includes('socketId')) {
          window.axios.interceptors.request.eject(index);
        }
      }
    });
  }
};

const createEchoInstance = (store) => {
  const token = store?.state?.auth?.access_token;

  if (!token) {
    console.warn("Echo: No auth token available");
    return null;
  }

  // Initialize Pusher only when actually needed
  initializePusher();

  // Temporarily hide window.axios so Echo doesn't add its interceptor
  const originalWindowAxios = window.axios;
  window.axios = undefined;

  const Echo = require("laravel-echo").default;

  if (echoInstance) {
    echoInstance.disconnect();
  }

  // Create a custom authorizer that uses axios instead of Pusher's internal XHR
  const authorizer = (channel) => {
    return {
      authorize: (socketId, callback) => {
        axios.post(
          `${process.env.VUE_APP_API_URL}/broadcasting/auth`,
          {
            socket_id: socketId,
            channel_name: channel.name
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            }
          }
        )
        .then(response => {
          callback(null, response.data);
        })
        .catch(error => {
          console.warn('Pusher auth error:', error);
          callback(error, null);
        });
      }
    };
  };

  echoInstance = new Echo({
    broadcaster: "pusher",
    key: process.env.VUE_APP_PUSHER_APP_KEY,
    cluster: process.env.VUE_APP_PUSHER_APP_CLUSTER,
    forceTLS: true,
    authorizer: authorizer,
    // Disable automatic connection on error to prevent interference with other requests
    enabledTransports: ['ws', 'wss'],
  });

  // Restore window.axios
  window.axios = originalWindowAxios;

  // Remove any X-Socket-Id interceptor that might have been added
  removeEchoAxiosInterceptor();

  // Handle connection errors gracefully
  echoInstance.connector.pusher.connection.bind('error', (err) => {
    console.warn('Pusher connection error:', err);
  });

  return echoInstance;
};

const getEcho = function() {
  if (!echoInstance) {
    return createEchoInstance(this.$store);
  }
  return echoInstance;
};

const disconnectEcho = () => {
  if (echoInstance) {
    echoInstance.disconnect();
    echoInstance = null;
  }
};

const EchoPlugin = {
  install(Vue) {
    Vue.prototype.$echo = getEcho;
    Vue.prototype.$echoConnect = function() {
      return createEchoInstance(this.$store);
    };
    Vue.prototype.$echoDisconnect = disconnectEcho;
  },
};

Vue.use(EchoPlugin);

export { getEcho, createEchoInstance, disconnectEcho };
export default EchoPlugin;
